import { parse, isValid as pslIsValid } from 'psl';
import { OFAC_TLD_LIST } from './consts';
import { DomainNameErrors } from '../../typing';
import { toASCII, toUnicode } from 'punycode/';
import iso639 from 'iso-639-1';
export { toASCII, toUnicode } from 'punycode/';
const TLDS_WHITE_LIST = ['ישראל', 'co.az', 'la.gov', 'hz.de', 'as', 'in.gov', 'll.land', 'co.pt', 'ag.org', 'sol.site', 'hku.hk', 'bank.in', 'fin.in', 'ai.kr', 'io.kr', 'bolivar.ms.us'];
export const isConsecutiveDotsInName = domain => domain.indexOf('..') >= 0;
export const isOFACTld = tld => OFAC_TLD_LIST.indexOf(tld.toLowerCase()) >= 0;
export const isDomainTooShort = domain => !domain || toUnicode(domain).length < 1;
export const parseDomain = domain_ => {
  if (!domain_) {
    return {
      error: DomainNameErrors.DOMAIN_TOO_SHORT,
      input: domain_
    };
  }
  if (domain_.input) {
    return domain_;
  }
  const domain = domain_;
  if (isConsecutiveDotsInName(domain)) {
    return {
      error: DomainNameErrors.TWO_CONSECUTIVE_DOTS,
      input: domain
    };
  }
  const formattedInput = formatInputPrefixAndMissingTld(domain);
  let parseResult = parse(formatDomainInput(domain));
  if ('error' in parseResult) {
    return {
      error: pslErrorCodeMap[parseResult.error.code],
      input: domain
    };
  }
  if (parseResult.sld == null) {
    return {
      error: DomainNameErrors.INVALID_DOMAIN_NAME,
      input: domain
    };
  }
  parseResult = detectSpecialTlds(parseResult);
  const {
    domain: main,
    tld,
    sld,
    subdomain,
    input
  } = parseResult;
  const isOFAC = tld && isOFACTld(tld);
  const isMainTooShort = isDomainTooShort(sld);
  const getInternalParsedErrors = () => {
    switch (true) {
      case isOFAC:
        return DomainNameErrors.OFAC_TLD;
      case isMainTooShort:
        return DomainNameErrors.DOMAIN_TOO_SHORT;
      default:
        return null;
    }
  };
  return {
    input,
    main: main,
    tld: tld,
    sld,
    subdomain: subdomain || null,
    error: getInternalParsedErrors(),
    isValidTld: isValidTld(parseResult),
    formattedInput: toUnicode(formattedInput),
    // ascii domain name is the "UUID" for DB persisting and referencing
    asciiDomain: toASCII(main),
    fullAsciiDomain: toASCII(formattedInput)
  };
};
const pslErrorCodeMap = {
  DOMAIN_TOO_SHORT: DomainNameErrors.DOMAIN_TOO_SHORT,
  DOMAIN_TOO_LONG: DomainNameErrors.DOMAIN_TOO_LONG,
  LABEL_STARTS_WITH_DASH: DomainNameErrors.INVALID_DASH_USAGE,
  LABEL_ENDS_WITH_DASH: DomainNameErrors.INVALID_DASH_USAGE,
  LABEL_TOO_LONG: DomainNameErrors.DOMAIN_TOO_LONG,
  LABEL_TOO_SHORT: DomainNameErrors.DOMAIN_TOO_SHORT,
  LABEL_INVALID_CHARS: DomainNameErrors.LABEL_INVALID_CHARS
};
export const extractTld = domain => {
  const parts = domain.split('.');
  return parts.length === 3 ? `${parts[1]}.${parts[2]}` : parts[1];
};
export const formatInputPrefixAndMissingTld = domain => {
  let trimmedInput = domain.trim();
  if (trimmedInput.endsWith('.')) {
    trimmedInput = trimmedInput.slice(0, -1);
  }
  const tld = extractTld(trimmedInput);
  if (!tld || tld === '.') {
    return `${trimmedInput}.com`.replace(/\.{2,}/, '.');
  }
  if (trimmedInput.startsWith('www.') && trimmedInput.split('.').length <= 2) {
    return `${trimmedInput}.com`;
  }
  return trimmedInput;
};
export const formatDomainInput = domain => {
  const formattedDomain = domain.trimEnd().toLowerCase().replace(/^(www\.)/, '');
  return formatInputPrefixAndMissingTld(formattedDomain);
};
export const areDomainsEqual = (a, b) => formatDomainInput(a) === formatDomainInput(b);
export const isValidTld = parsedDomain => {
  if (!parsedDomain.tld) {
    return false;
  }
  const isValid = pslIsValid(parsedDomain.input);
  if (!isValid && TLDS_WHITE_LIST.includes(parsedDomain.tld)) {
    return true;
  }
  return isValid;
};

/**
 This function is validating a query with a potential of multiple words,
 we want to validate the query as a domain name if it is a single word and analyze it as a domain name
 Example: "test.com" -> true
 Example: "test.com test.com" -> false

 It will still return true if it's falls on domain validation rules (ofac, too short, too long, etc.) it's up to the caller to handle the error.
 */
export const isValidDomain = query => {
  const domain = query.trim();
  if (!domain || domain.split(' ').length > 1) {
    return false;
  }
  const parsedDomain = parseDomain(query);
  const containsTldExtendedValidation = query.split('.').length > 1;
  const tldValidation = Boolean(containsTldExtendedValidation);
  const isDomainValid = tldValidation && (![DomainNameErrors.GENERAL_ERROR].includes(parsedDomain.error) || [DomainNameErrors.OFAC_TLD, DomainNameErrors.TWO_CONSECUTIVE_DOTS, DomainNameErrors.DOMAIN_TOO_SHORT, DomainNameErrors.DOMAIN_TOO_LONG, DomainNameErrors.LABEL_INVALID_CHARS, DomainNameErrors.INVALID_DASH_USAGE].includes(parsedDomain.error));
  return Boolean(isDomainValid);
};
const ALL_COUNTRY_CODES = iso639.getAllCodes();
const isCountryCode = value => {
  const allCountryCodes = ALL_COUNTRY_CODES;
  const countryCode = allCountryCodes.find(code => code === value);
  return !!countryCode;
};
export const isCountryCodeSubdomain = domain => {
  const parsedDomain = domain.main ? domain : parseDomain(domain);
  if (!(parsedDomain != null && parsedDomain.subdomain)) {
    return false;
  }
  const subdomainFirstPart = parsedDomain.subdomain.split('.')[0];
  return isCountryCode(subdomainFirstPart);
};
const detectSpecialTlds = parsedDomain => {
  var _formatDomainInput;
  const parts = ((_formatDomainInput = formatDomainInput(parsedDomain.input)) == null ? void 0 : _formatDomainInput.split('.')) ?? [];
  if (parts.length < 2) {
    return parsedDomain;
  }

  // Try the longest whitelisted suffix first so multi-label special TLDs
  // (e.g. `bolivar.ms.us`) win over shorter matches, leaving at least one
  // label for the sld.
  for (let labels = parts.length - 1; labels >= 1; labels--) {
    const tld = parts.slice(-labels).join('.');
    if (!TLDS_WHITE_LIST.includes(tld)) {
      continue;
    }
    const rest = parts.slice(0, -labels);
    const sld = rest.pop() ?? null;
    if (!sld) {
      break;
    }
    return {
      ...parsedDomain,
      tld,
      sld,
      domain: `${sld}.${tld}`,
      subdomain: rest.join('.')
    };
  }
  return parsedDomain;
};
export const getIsComBrRestricted = domain => {
  if (!domain) {
    return false;
  }
  const parsedDomain = typeof domain === 'string' ? parseDomain(domain) : domain;
  return parsedDomain.tld === 'com.br';
};
//# sourceMappingURL=parser.js.map